// The day, on the ground.
//
// Roads come out of the same static route index that orders the rounds
// and works out the arrivals, read by byte range — so this map cannot
// draw a street the router does not believe in, and there is no tile
// server, no account and no third party watching a dispatcher pan around
// their own city.
//
// **It draws only what is true** (DESIGN: *maps that only draw what is
// true*). A vehicle is drawn when, and only when, the honesty model says
// the position claim is `live`. A coarse run publishes no position by
// design and gets no pin — not a stale one, not a guessed one, none —
// and a run that has gone quiet loses its pin rather than leaving it
// somewhere the van no longer is. Everything on this surface is
// therefore something the run itself said, recently, on purpose.
//
// Amber is the vehicle and nothing else, which is the whole rule the
// brand file states: a map where four things are amber is a map where
// amber means "thing".

import { useEffect, useMemo, useRef, useState } from "react";
import {
  BASEMAP_LEAVES,
  ROAD_LAYERS,
  classOf,
  detailFor,
  geoAtClient,
  gestureExtent,
  polyline,
  projector,
  zoomAbout,
  zoomStep,
  type MapExtent,
  type MapPoint
} from "@wayfind/core";
import type { RouteEngine } from "@wayfind/core";

export interface MapVehicle {
  id: string;
  lat: number;
  lon: number;
  label: string;
  /** The one being looked at, drawn larger and labelled. */
  focused: boolean;
  /**
   * Degrees clockwise from north, when the run said so. A heading is a
   * claim like any other: absent means the chevron is not drawn, not that
   * it points north.
   */
  headingDegrees?: number | null;
}

export interface MapStop {
  id: string;
  lat: number;
  lon: number;
  label: string;
  /** Settled stops are drawn as outcomes, pending ones as plain marks. */
  tone: "pending" | "delivered" | "missed";
  focused: boolean;
}

/**
 * The route under a run, split the way the mark is.
 *
 * `track` is everything the run still has to drive; `range` is the leg it
 * is driving *now*. Both are real geometry from the same router that
 * produced the arrival times — this surface draws no path it was not
 * given, so a round with no live position contributes neither.
 *
 * Every round out gets a track. Only the **selected** one gets its range
 * drawn in amber, which is the rule the brand file states plainly: if two
 * things on a screen are amber, one of them is lying about being the live
 * thing. Ten amber roads would mean "road".
 */
export interface MapLeg {
  id: string;
  track: [number, number][];
  range: [number, number][];
  selected: boolean;
}

interface Road { points: [number, number][]; roadClass: string }

type Extent = MapExtent;

/*
  What this console allows itself.

  The arithmetic is shared with the consumer app through `@wayfind/core`;
  these are the parts that are a *dispatcher's* opinion rather than a
  map's. A console frames a fleet across a region, so it zooms out
  further than a page tracking one parcel does.
*/
const LIMITS = { min: 0.004, max: 0.9 };

/** The drawing box every projection and gesture is expressed in. */
const MAP_WIDTH = 1000;
const MAP_HEIGHT = 640;

/**
 * Pointer capture, which is allowed to fail.
 *
 * Both calls throw `NotFoundError` when the browser no longer considers
 * the pointer active — a touch already lifted, a capture stolen by
 * something else, a synthetic event in a test. Capture is an optimisation
 * on top of a gesture that already tracks pointers itself, so losing it
 * costs nothing and throwing out of a React handler costs the render.
 */
function capture(element: Element | null, pointerId: number, take: boolean): void {
  if (!element) return;
  try {
    if (take) element.setPointerCapture(pointerId);
    else element.releasePointerCapture(pointerId);
  } catch {
    // Not captured, or no longer capturable. The gesture survives either way.
  }
}

/** The box that holds every point, with room to breathe. */
function extentOf(points: { lat: number; lon: number }[]): Extent | null {
  if (!points.length) return null;
  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const point of points) {
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lon)) continue;
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLon = Math.min(minLon, point.lon);
    maxLon = Math.max(maxLon, point.lon);
  }
  if (!Number.isFinite(minLat)) return null;
  // A single point has no span, and a zero span is a division by zero
  // two lines down. The floor is about 400 m, which is a street.
  const spanLat = Math.max(maxLat - minLat, LIMITS.min);
  const spanLon = Math.max(maxLon - minLon, LIMITS.min * 1.9);
  const padLat = spanLat * 0.25;
  const padLon = spanLon * 0.25;
  const midLat = (minLat + maxLat) / 2;
  const midLon = (minLon + maxLon) / 2;
  return {
    minLat: midLat - spanLat / 2 - padLat,
    maxLat: midLat + spanLat / 2 + padLat,
    minLon: midLon - spanLon / 2 - padLon,
    maxLon: midLon + spanLon / 2 + padLon
  };
}

export function FleetMap({
  engine,
  vehicles,
  stops,
  legs,
  onPick
}: {
  engine: RouteEngine | null;
  vehicles: MapVehicle[];
  stops: MapStop[];
  /** Remaining road per round that has one, in [lat, lon] order. */
  legs?: MapLeg[];
  onPick?(id: string): void;
}): JSX.Element {
  const [roads, setRoads] = useState<Road[]>([]);
  const [partial, setPartial] = useState(false);
  const [held, setHeld] = useState<Extent | null>(null);
  /* The fitted view, readable from a listener that outlives this render. */
  const fittedRef = useRef<Extent | null>(null);
  const frame = useRef<HTMLDivElement | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());

  // What the day covers, or what the operator dragged to. Recomputed from
  // the marks rather than remembered, so a van that moves keeps itself in
  // frame until somebody takes the map away from it by hand.
  const fitted = useMemo(
    () => extentOf([...vehicles, ...stops]),
    [vehicles, stops]
  );
  const extent = held ?? fitted;
  fittedRef.current = fitted;

  /*
    Wheel is bound by hand, not through React, for one reason: it has to
    be able to call `preventDefault()`.

    React attaches wheel passively, and a passive listener cannot stop
    the browser's own behaviour. On macOS a trackpad pinch arrives as a
    wheel event with `ctrlKey` set, and the default behaviour for that is
    to zoom the entire page — so pinching the map was zooming the console
    around it. Bound directly with `passive: false`, the gesture belongs
    to the map.
  */
  const hasMap = Boolean(extent);
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const onWheel = (event: WheelEvent): void => {
      const fallback = fittedRef.current;
      if (!fallback) return;
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const factor = zoomStep(event);
      const { clientX, clientY } = event;
      setHeld(current => {
        const base = current ?? fallback;
        const { unproject } = projector(base, MAP_WIDTH, MAP_HEIGHT);
        // Zoom about the cursor, not the middle of the frame: a wheel
        // aimed at a street should bring that street closer, not slide
        // it away while something else grows.
        return zoomAbout(
          base, factor,
          geoAtClient(clientX, clientY, rect, MAP_WIDTH, MAP_HEIGHT, unproject),
          LIMITS
        );
      });
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => { element.removeEventListener("wheel", onWheel); };
  }, [hasMap]);

  /*
    Roads for the area in view, plus a screen of margin in every
    direction.

    Fetching exactly the visible box meant the first pixel of a drag
    exposed ground whose roads had never been asked for, and the refetch
    key was quantised to a flat 0.005 degrees — about 550 m — so the
    reveal could run for a couple of hundred metres before anything went
    looking for them. That is the empty strip: not missing data, just
    data nobody had requested yet.

    So the box is grown by `MARGIN` and the key is quantised to a
    fraction of the *current* span rather than a fixed number of degrees.
    That keeps the dependency stable while a vehicle creeps down a street
    at any zoom, and guarantees the quantisation step stays far smaller
    than the margin — which is the property that actually prevents gaps.
  */
  /*
    Margin is not free. The engine bounds what it returns, so every extra
    degree fetched is a degree competing for the same budget: at 0.6 the
    box is 2.2x wider *and* taller — nearly five times the area — which
    made `truncated` fire on an ordinary three-town board and put holes
    back in the middle of the map to buy margin at its edges. A quarter
    of a screen each way covers a normal drag and costs 2.25x rather
    than 4.8x.
  */
  const MARGIN = 0.25;
  const QUANTUM = 12;
  const fetchBox = useMemo(() => {
    if (!extent) return null;
    const padLat = (extent.maxLat - extent.minLat) * MARGIN;
    const padLon = (extent.maxLon - extent.minLon) * MARGIN;
    return {
      minLat: extent.minLat - padLat, maxLat: extent.maxLat + padLat,
      minLon: extent.minLon - padLon, maxLon: extent.maxLon + padLon
    };
  }, [extent?.minLat, extent?.maxLat, extent?.minLon, extent?.maxLon]);

  const boxKey = useMemo(() => {
    if (!fetchBox) return "";
    const stepLat = Math.max((fetchBox.maxLat - fetchBox.minLat) / QUANTUM, 1e-9);
    const stepLon = Math.max((fetchBox.maxLon - fetchBox.minLon) / QUANTUM, 1e-9);
    return [
      Math.round(fetchBox.minLat / stepLat), Math.round(fetchBox.maxLat / stepLat),
      Math.round(fetchBox.minLon / stepLon), Math.round(fetchBox.maxLon / stepLon)
    ].join(",");
  }, [fetchBox]);

  useEffect(() => {
    if (!engine || !fetchBox) { setRoads([]); setPartial(false); return; }
    let live = true;
    void engine
      .roadsIn(fetchBox, { maxLeaves: BASEMAP_LEAVES })
      .then(result => {
        if (!live) return;
        setRoads(result.roads);
        // The engine bounds what it will return. When it has, the map is
        // genuinely incomplete, and a basemap with holes that says
        // nothing is a map lying by omission — so it says so instead.
        setPartial(result.truncated);
      })
      .catch(() => { if (live) { setRoads([]); setPartial(false); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, boxKey]);

  const width = MAP_WIDTH;
  const height = MAP_HEIGHT;

  if (!extent) {
    return (
      <div className="fleetmap fleetmap--empty">
        <p className="small muted">
          Nothing to draw yet. A vehicle appears here when a run publishes a position, and the
          doors appear as soon as a {"job"} is sent.
        </p>
      </div>
    );
  }

  const { project, widthKm } = projector(extent, width, height);
  const detail = detailFor(widthKm);

  /*
    Composed on the newest extent, not the rendered one.

    A finger produces pointer events far faster than React re-renders, so
    reading `held` from the closure meant a burst of moves all measured
    from the same stale view and the last one silently won. A five-fold
    pinch arrived as a 2.2-fold zoom. The functional update makes every
    event build on the result of the one before it, whatever the renderer
    is doing.
  */
  const applyGesture = (before: MapPoint[], after: MapPoint[]): void => {
    const rect = frame.current?.getBoundingClientRect();
    if (!rect || !fitted) return;
    setHeld(current => gestureExtent(
      current ?? fitted, before, after, rect,
      { width: MAP_WIDTH, height: MAP_HEIGHT, limits: LIMITS }
    ));
  };

  const trace = (points: [number, number][]): string => points.map((point, index) => {
    const [x, y] = project(point[0], point[1]);
    return `${index === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");

  return (
    <div
      className="fleetmap"
      ref={frame}
      onPointerDown={event => {
        // Only the first two fingers steer. A palm landing third would
        // otherwise drag the midpoint off across the map.
        if (pointers.current.size >= 2) return;
        capture(frame.current, event.pointerId, true);
        pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      }}
      onPointerMove={event => {
        if (!pointers.current.has(event.pointerId)) return;
        // Sorted by pointer id so "first" and "second" mean the same
        // finger in both snapshots, which is what makes the spread
        // between them comparable.
        const ids = [...pointers.current.keys()].sort((a, b) => a - b);
        const before = ids.map(id => pointers.current.get(id)!);
        pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const after = ids.map(id => pointers.current.get(id)!);
        applyGesture(before, after);
      }}
      onPointerUp={event => {
        capture(frame.current, event.pointerId, false);
        pointers.current.delete(event.pointerId);
      }}
      onPointerCancel={event => { pointers.current.delete(event.pointerId); }}
    >
      <svg
        className="fleetmap__canvas"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid slice"
        role="img"
        aria-label="The roads this console holds, the doors on today's rounds, and any vehicle publishing a position."
      >
        {/*
          Back to front, so a motorway sits over a service road. Without
          the ordering a street map reads as a mat of equal lines.
        */}
        {ROAD_LAYERS.map(layer => {
          if (!detail.has(layer)) return null;
          const inLayer = roads.filter(road => classOf(road.roadClass) === layer);
          if (!inLayer.length) return null;
          return (
            <path
              key={layer}
              className={`fleetmap__road fleetmap__road--${layer}`}
              d={inLayer.map(road => polyline(road.points, project)).filter(Boolean).join(" ")}
            />
          );
        })}

        {/*
          Every track, then the one selected range over them all. Drawn
          before the doors so a stop mark is never buried under the road
          that leads to it.
        */}
        {(legs ?? []).filter(entry => entry.track.length).map(entry => (
          <path
            key={`casing-${entry.id}`}
            className="fleetmap__leg fleetmap__leg--casing"
            d={trace(entry.track)}
          />
        ))}
        {(legs ?? []).filter(entry => entry.track.length).map(entry => (
          <path
            key={`track-${entry.id}`}
            className={`fleetmap__leg fleetmap__leg--track${entry.selected ? " is-selected" : ""}`}
            d={trace(entry.track)}
          />
        ))}
        {(legs ?? [])
          .filter(entry => entry.selected && entry.range.length)
          .map(entry => (
            <path
              key={`range-${entry.id}`}
              className="fleetmap__leg fleetmap__leg--range"
              d={trace(entry.range)}
            />
          ))}

        {stops.map(stop => {
          const [x, y] = project(stop.lat, stop.lon);
          return (
            <g key={stop.id} className={`fleetmap__stop fleetmap__stop--${stop.tone}`}>
              <circle cx={x} cy={y} r={stop.focused ? 9 : 6} />
              {stop.focused ? (
                <text x={x + 13} y={y + 4} className="fleetmap__label">{stop.label}</text>
              ) : null}
            </g>
          );
        })}

        {vehicles.map(vehicle => {
          const [x, y] = project(vehicle.lat, vehicle.lon);
          const radius = vehicle.focused ? 12 : 9;
          return (
            <g
              key={vehicle.id}
              className="fleetmap__van"
              onClick={() => onPick?.(vehicle.id)}
              style={{ cursor: onPick ? "pointer" : undefined }}
            >
              {/*
                Keyed on the position itself, so React tears the circle
                down and builds a new one when — and only when — a fix
                lands. That restart is the whole animation: one ping per
                thing the run actually said.
              */}
              <circle
                key={`${vehicle.lat.toFixed(5)},${vehicle.lon.toFixed(5)}`}
                className="fleetmap__ping"
                cx={x}
                cy={y}
                r={radius}
              />
              <circle className="fleetmap__halo" cx={x} cy={y} r={radius + 6} />
              {Number.isFinite(vehicle.headingDegrees ?? NaN) ? (
                // An arrowhead with a notched tail: directional at a
                // glance, and still obviously one object at 9 px.
                // SVG's 0° points east and a compass bearing points
                // north, hence the quarter turn.
                <path
                  className="fleetmap__vanbody"
                  transform={`translate(${x} ${y}) rotate(${(vehicle.headingDegrees ?? 0) - 90})`}
                  d={`M ${radius * 1.15} 0
                      L ${-radius * 0.75} ${radius * 0.92}
                      L ${-radius * 0.34} 0
                      L ${-radius * 0.75} ${-radius * 0.92} Z`}
                />
              ) : (
                // No heading claimed, so none drawn.
                <circle className="fleetmap__vanbody" cx={x} cy={y} r={radius * 0.82} />
              )}
              {vehicle.focused ? (
                <text x={x + radius + 7} y={y - radius + 2} className="fleetmap__label fleetmap__label--van">
                  {vehicle.label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>

      {/*
        Genuinely incomplete, as opposed to deliberately simplified. The
        level-of-detail rules above drop small roads on purpose and say
        nothing; this is the other case — the engine hit its own bound
        and the ground really is missing roads it holds.
      */}
      {partial ? (
        <p className="fleetmap__partial" role="status">
          More map here than this view can draw. Zoom in to see all of it.
        </p>
      ) : null}

      {held ? (
        <button
          type="button"
          className="button button--small fleetmap__recentre"
          onClick={() => setHeld(null)}
        >
          Fit the day
        </button>
      ) : null}
    </div>
  );
}
